import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api } from '../lib/api'
import type { Meta, Sport } from '../lib/types'

export type NavLinkItem = { label: string; to: string; sub?: string }
export type NavColumn = { title: string; accent: string; color: string; to: string; items: NavLinkItem[] }
export type NavEntry = { id: string; label: string; columns?: NavColumn[]; items?: NavLinkItem[] }

export function buildMenu(meta: Meta): NavEntry[] {
  const subs = meta.subcategories
  const label = (id: string, l?: string) => l || subs[id] || id
  const catItems = (s: Sport) => s.categories.filter((c) => subs[c.sub]).map((c) => ({ label: label(c.sub, c.label), sub: c.sub }))
  const link = (l: string) => { const [name, to] = l.split('|').map((x) => x.trim()); return { label: name, to: to || '/' } }
  return meta.rubriques.map((r) => r.type === 'sports'
    ? { id: r.id, label: r.label, columns: meta.sports.filter((s) => s.kind === (r.sport_kind || r.id)).map((s) => ({ title: s.label, accent: s.id, color: s.color, to: `/sport/${s.id}`, items: catItems(s).map((i) => ({ ...i, to: `/sport/${s.id}/${i.sub}` })) })) }
    : { id: r.id, label: r.label, items: [...r.items.filter((i) => subs[i.sub]).map((i) => ({ label: label(i.sub, i.label), sub: i.sub, to: `/${r.id}/${i.sub}` })), ...r.links.map(link)] })
}

/** Paramètres de catalogue pour une rubrique de la recherche (sport co ou entrée du menu). */
export function rubriqueParams(menu: NavEntry[], id: string): string {
  if (!id) return ''
  const entry = menu.find((e) => e.id === id)
  if (entry) return (entry.items || entry.columns!.flatMap((c) => c.items)).filter((i) => i.sub).map((i) => `sub=${i.sub}`).join('&')
  return `sport=${id}`
}

type ShopCtx = { meta: Meta | null; menu: NavEntry[]; sportInfo: (id: string) => { label: string; emoji: string; color: string } }
const Ctx = createContext<ShopCtx>({ meta: null, menu: [], sportInfo: () => ({ label: '', emoji: '', color: '#64748b' }) })

export function ShopProvider({ children }: { children: ReactNode }) {
  const [meta, setMeta] = useState<Meta | null>(null)
  useEffect(() => {
    api<Meta>('/boutique/meta').then(setMeta).catch(() => setMeta(null))
  }, [])
  const value = useMemo<ShopCtx>(() => ({
    meta,
    menu: meta ? buildMenu(meta) : [],
    sportInfo: (id) => meta?.sports.find((s) => s.id === id) ?? { label: 'Multisport', emoji: '🏆', color: '#64748b' },
  }), [meta])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export const useShop = () => useContext(Ctx)
